/**
 * Component swap capture for serialized assembly jobs.
 *
 * TODO(assembly-component-swap): Persist swap selections on assemble and adjust the work order BOM
 * allocations for swapped components.
 * TODO(assembly-component-swap): Post Megaventory adjustment plus / adjustment minus for each swapped
 * component quantity after the work order update succeeds.
 */

import { Loader2 } from "lucide-react";

import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { CardTitleWithHelp } from "@/components/ui/title-help";
import type { HubBomLine } from "./assemblyApi";
import {
  assemblySwapSlotIsChecked,
  assemblySwapSlotSkuSummary,
  type AssemblyComponentSwapSlot,
} from "./assemblyComponentSwap";

const EXISTING_TLA_SWAP_HELP =
  "For an existing TLA conversion, check only the battery pack or potted clamp body you physically replaced on this unit. " +
  "Leave boxes unchecked when the installed parts already match the bill of materials. " +
  "Inventory adjustments are not applied yet — selections are captured for a future release.";

export function AssemblyComponentSwapSection({
  lines,
  swapSlots,
  bomNote,
  swappedVariableNames,
  onToggleSlot,
  disabled,
  loading,
  errorMessage,
}: {
  lines: HubBomLine[];
  swapSlots: AssemblyComponentSwapSlot[] | null;
  bomNote?: string | null;
  swappedVariableNames: ReadonlySet<string>;
  onToggleSlot: (variableNames: readonly string[]) => void;
  disabled?: boolean;
  loading?: boolean;
  errorMessage?: string | null;
}): JSX.Element | null {
  if (swapSlots == null || swapSlots.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitleWithHelp hoverHint={EXISTING_TLA_SWAP_HELP}>Component swaps</CardTitleWithHelp>
      </CardHeader>
      <CardBody className="space-y-2">
        {loading ? (
          <p className="text-sm text-slate-500">
            <Loader2 className="mr-1 inline h-4 w-4 animate-spin" aria-hidden /> Loading bill of materials…
          </p>
        ) : errorMessage != null && errorMessage.trim() !== "" ? (
          <p className="text-sm text-amber-800">{errorMessage}</p>
        ) : (
          <>
            <p className="text-xs text-slate-600">
              Check only the battery or potted clamp body you replaced on this unit. Leave boxes unchecked when parts are
              already correct.
            </p>
            {bomNote?.trim() ? <p className="text-xs text-slate-500">{bomNote.trim()}</p> : null}
            <ul className="space-y-2">
              {swapSlots.map((slot) => {
                const checked = assemblySwapSlotIsChecked(swappedVariableNames, slot.variable_names);
                const skuSummary = assemblySwapSlotSkuSummary(lines, slot.variable_names);
                return (
                  <li key={slot.id}>
                    <label className="flex cursor-pointer items-start gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50">
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => onToggleSlot(slot.variable_names)}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-slate-900">{slot.label}</span>
                        <span className="block font-mono text-xs text-slate-700">{skuSummary}</span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </CardBody>
    </Card>
  );
}
